import { useState, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/pages/login.css';
import EnergyFlowBackground from '../components/EnergyFlowBackground';
import { useI18n, LanguageSwitcher } from '../i18n';
import { ThemeSwitcher } from '../theme';
import type { TranslationKey } from '../i18n/translations';

// Messages d'erreur connus de l'API (renvoyés en français) → clés traduites
const API_ERRORS: Record<string, TranslationKey> = {
  'Email ou mot de passe incorrect.': 'login_err_invalid',
  "Compte désactivé. Contactez l'administrateur.": 'login_err_disabled',
  'Échec de la connexion': 'login_err_failed',
};

/** Rend les segments **gras** d'une chaîne traduite. */
function rich(text: string) {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}

const AUTH_API_URL = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState<{ key: TranslationKey } | { raw: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  // ── Forgot password modal state ──
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '';

  /**
   * Role-based redirect after login:
   * - super_admin → /admin
   * - installer_admin / installer_technician → /my-clients
   * - org_admin / maintenance / user → /dashboard (= /)
   */
  function getRedirectPath(userData: any): string {
    if (from) return from; // respect intended destination if set

    if (userData?.platformRole === 'super_admin') return '/admin';

    const membership = userData?.memberships?.[0];
    const role = membership?.role || '';
    if (role === 'installer_admin' || role === 'installer_technician') return '/my-clients';

    return '/';
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await login(email, password);
      // Get user from localStorage since state hasn't updated yet
      const savedUser = localStorage.getItem('dashboard_zce_user');
      const userData = savedUser ? JSON.parse(savedUser) : null;
      navigate(getRedirectPath(userData), { replace: true });
    } catch (err) {
      const raw = err instanceof Error ? err.message : '';
      // fetch() en échec réseau lève un TypeError (« Failed to fetch »)
      // on garde la clé (ou le texte brut de l'API) pour que le message suive un changement de langue
      setError(err instanceof TypeError ? { key: 'login_err_network' } : API_ERRORS[raw] ? { key: API_ERRORS[raw] } : raw ? { raw } : { key: 'login_err_generic' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open the forgot-password modal pre-filled with the login email
  const openForgot = () => {
    setForgotEmail(email);
    setForgotMessage(null);
    setForgotError(null);
    setForgotOpen(true);
  };

  const closeForgot = () => {
    setForgotOpen(false);
    // Reset state after close animation
    setTimeout(() => {
      setForgotMessage(null);
      setForgotError(null);
      setForgotSubmitting(false);
    }, 200);
  };

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotSubmitting(true);
    setForgotError(null);
    setForgotMessage(null);
    try {
      const res = await fetch(`${AUTH_API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim().toLowerCase() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        // API can sometimes return an object/array for error → coerce to string safely
        // messages de l'API en français → messages traduits selon le statut
        let msg = t('forgot_err_generic');
        if (res.status === 400) msg = t('forgot_err_invalidEmail');
        else if (res.status === 429) msg = t('forgot_err_rate');
        else if (res.status >= 500) msg = t('forgot_err_unavailable');
        throw new Error(msg);
      }
      setForgotMessage(t('forgot_sentText'));
    } catch (err) {
      setForgotError(err instanceof TypeError ? t('login_err_network') : err instanceof Error ? err.message : t('forgot_err_generic'));
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="login-page zce-gradient-anim">
      {/* Animated background orbs */}
      <div className="login-orb login-orb--top zce-float-orb" />
      <div className="login-orb login-orb--bottom zce-float-orb" />
      <div className="login-orb login-orb--center zce-float-orb" />
      {/* Réseau électrique animé (même fond que le Command Center) */}
      <EnergyFlowBackground className="opacity-70" />

      {/* Card */}
      <div className="login-card-wrapper zce-fade-in">
        <div className="login-card relative">
          {/* Sélecteur de langue, dans la carte pour être vu tout de suite */}
          <div className="absolute top-5 right-5 z-10 flex items-center gap-2"><ThemeSwitcher /><LanguageSwitcher /></div>
          {/* Top accent bar */}
          <div className="login-card__accent-bar" />

          <div className="login-card__content">
            {/* Logo / Header */}
            <div className="login-header">
              <div className="login-logo">
                <svg className="login-logo__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h1 className="login-title">ZOS</h1>
              <p className="login-subtitle">{t('login_subtitle')}</p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="login-error zce-fade-in">
                <svg className="login-error__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="login-error__text">{'key' in error ? t(error.key) : error.raw}</span>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="login-form">
              <div>
                <label htmlFor="email" className="login-label">
                  {t('login_email')}
                </label>
                <div className="login-input-wrapper">
                  <span className="login-input-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="login-input"
                    placeholder={t('login_emailPlaceholder')}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div>
                <div className="login-password-header">
                  <label htmlFor="password" className="login-label">
                    {t('login_password')}
                  </label>
                  <button
                    type="button"
                    onClick={openForgot}
                    className="login-forgot-link"
                  >
                    {t('login_forgot')}
                  </button>
                </div>
                <div className="login-input-wrapper">
                  <span className="login-input-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </span>
                  <input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    className="login-input login-input--password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPwd((s) => !s)}
                    className="login-password-toggle"
                    aria-label={showPwd ? t('login_hidePwd') : t('login_showPwd')}
                  >
                    {showPwd ? (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="login-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <svg className="login-spinner" fill="none" viewBox="0 0 24 24">
                      <circle className="login-spinner__circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="login-spinner__path" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('login_submitting')}
                  </>
                ) : (
                  <>
                    {t('login_submit')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="login-divider">
              <div className="login-divider__line" />
              <span className="login-divider__text">{t('login_or')}</span>
              <div className="login-divider__line" />
            </div>

            {/* Footer */}
            <div className="login-footer">
              <p className="login-footer__text">
                {t('login_team')}
              </p>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                className="login-signup-btn"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                {t('login_requestOrg')}
              </button>
              <p className="login-copyright">
                {t('login_copyright')}
              </p>
            </div>
          </div>
        </div>

        {/* Trust badge */}
        <div className="login-trust-badge">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <span>{t('login_trust')}</span>
        </div>
      </div>

      {/* ── Mot de passe oublié modal ── */}
      {forgotOpen && (
        <div className="login-modal-overlay zce-fade-in" onClick={closeForgot}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <div className="login-modal__header">
              <div className="flex items-center gap-3">
                <div className="login-modal__header-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <h3 className="login-modal__header-title">{t('forgot_title')}</h3>
              </div>
              <button onClick={closeForgot} aria-label={t('forgot_close')} className="login-modal__close-btn">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* ── Success state ── */}
            {forgotMessage ? (
              <div className="login-modal__success">
                <div className="login-modal__success-icon-wrapper">
                  <div className="login-modal__success-icon">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="text-center">
                  <h4 className="login-modal__success-title">{t('forgot_sentTitle')}</h4>
                  <p className="login-modal__success-text">{forgotMessage}</p>
                </div>
                <div className="login-modal__info-box">
                  <p>{t('forgot_checkInbox')}</p>
                  <ul>
                    <li>{rich(t('forgot_tip1'))}</li>
                    <li>{rich(t('forgot_tip2'))}</li>
                    <li>{rich(t('forgot_tip3'))}</li>
                  </ul>
                </div>
                <button onClick={closeForgot} className="login-modal__close-full-btn">
                  {t('forgot_close')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="login-modal__form">
                <p className="login-modal__form-description">
                  {t('forgot_desc')}
                </p>

                <div>
                  <label htmlFor="forgot-email" className="login-label">
                    {t('login_email')}
                  </label>
                  <div className="login-input-wrapper">
                    <span className="login-input-icon">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      autoFocus
                      disabled={forgotSubmitting}
                      className="login-input"
                      placeholder={t('login_emailPlaceholder')}
                      autoComplete="email"
                    />
                  </div>
                </div>

                {forgotError && (
                  <div className="login-modal__error">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{forgotError}</span>
                  </div>
                )}

                <div className="login-modal__warning-box">
                  <p>{t('forgot_privacy')}</p>
                </div>

                <div className="login-modal__actions">
                  <button
                    type="button"
                    onClick={closeForgot}
                    disabled={forgotSubmitting}
                    className="login-modal__cancel-btn"
                  >
                    {t('forgot_cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={forgotSubmitting || !forgotEmail.trim()}
                    className="login-modal__send-btn"
                  >
                    {forgotSubmitting ? (
                      <>
                        <svg className="login-modal__send-spinner" fill="none" viewBox="0 0 24 24">
                          <circle className="login-spinner__circle" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="login-spinner__path" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        {t('forgot_sending')}
                      </>
                    ) : (
                      <>
                        {t('forgot_send')}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>

                <p className="login-modal__help-text">
                  {t('forgot_help')}{' '}
                  <a href="mailto:support@zce-group.com" className="login-modal__help-link">
                    support@zce-group.com
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
